import { BandChart } from '@design/charts/band-chart/BandChart';
import { getHorizonToIndexMap } from '@terminal/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useThemePerformance } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useThemePerformanceTimeseries } from '@terminal/react-query-hooks/themes/useThemePerformanceTimeseries';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { HorizonSelector } from '@terminal/widgets/theme-widget/tabs/theme-overview-tab/components/horizon-selector/HorizonSelector';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartTitle } from './chart-title/ChartTitle';
import styles from './ThemePerformanceSection.module.scss';
import { ThemePerformanceSectionContent } from './ThemePerformanceSectionContent';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

type ThemePerformanceSectionProps = {
  entityTags: string[];
  customHeaderDescription?: ReactNode;
  isFetching?: boolean;
  isError?: boolean;
  error?: Error | null;
};

export const ThemePerformanceSection = ({
  entityTags,
  customHeaderDescription,
  isFetching,
  isError,
  error = null,
}: ThemePerformanceSectionProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const { params } = useQueryParams();
  const activeHorizon = params[queryKeys.themeHorizon];
  const themeId = params[queryKeys.themeId];
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );
  const isIntraday = isIntradayChartEnabled && activeHorizon === 'intraday';

  const horizonToIndexMap = getHorizonToIndexMap(1);

  const {
    data: calculatorPerformance,
    isFetching: isCalculatorFetching,
    isError: isCalculatorError,
    error: calculatorError,
  } = useThemePerformance(
    entityTags,
    horizonToIndexMap.get(activeHorizon?.toLowerCase() ?? '1y'),
    !isIntraday
  );

  const {
    data: intradayPerformance,
    isFetching: isIntradayFetching,
    isError: isIntradayError,
    error: intradayError,
  } = useThemePerformanceTimeseries(
    { themeId: themeId ?? '', horizon: 'intraday' },
    isIntraday && !!themeId
  );

  const aggregate = isIntraday
    ? intradayPerformance?.aggregate
    : calculatorPerformance?.aggregate;
  const isThemePerformanceFetching = isIntraday
    ? isIntradayFetching
    : isCalculatorFetching;
  const isThemePerformanceError = isIntraday
    ? isIntradayError
    : isCalculatorError;
  const themePerformanceError = isIntraday ? intradayError : calculatorError;

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: { index: string; value: number }[]) =>
    data?.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const bandChartWrapperStyle: Record<string, string> = useMemo(
    () => ({
      '--band-chart-line-color': colors.default.value,
    }),
    [colors.default.value]
  );

  return (
    <ThemePerformanceSectionContent
      isLoading={isThemePerformanceFetching || !!isFetching}
      isError={isThemePerformanceError || !!isError}
      isEmpty={!aggregate?.length}
      error={!!isError ? error : themePerformanceError}
    >
      <div className={styles.chartAndHorizonsWrapper}>
        <HorizonSelector />
        <ChartTitle
          description={
            customHeaderDescription ??
            `${t('themeOverview.equalWeightedBasket')} ${t(
              'themeOverview.topCompanies',
              { total }
            )}`
          }
        />
        <div className={styles.bandChartWrapper} style={bandChartWrapperStyle}>
          <BandChart
            tsData={formatData(aggregate)}
            showLine
            domainLimited={false}
            includeXScalePadding={false}
            isBordered
            fillColors={{
              positive: colors.threshold.positive.value,
              negative: colors.threshold.negative.value,
            }}
            isPercent
            locale={languageSetting}
          />
        </div>
      </div>
    </ThemePerformanceSectionContent>
  );
};
